import React, {useCallback, useEffect} from 'react';
import {View, ScrollView} from 'react-native';

import {StyleSheet} from 'react-native';
import {colorPalette} from '../../../core/config/color.config';

import {useFocusEffect, useNavigation} from '@react-navigation/native';
import {NavigationKey} from '../../navigations/config';

import {
  RNEText,
  SVGIcon,
  RNEButton,
  RNEInput,
  ImageSelector,
} from '../../components';
import {useToast} from 'react-native-fast-toast';

import {useForm, Controller} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';

import useStores from '../../stores/useStores';
import {EditAccountReqBodyExt} from '../../server/types/account.types';
import {useEditAccount} from '../../server/react-query/useAccount';
import {formatDateString} from '../../utils/date.utils';
import {useState} from 'react';
import {
  PatientProfileReqBody,
  UploadPatientProfilePhotoReqBody,
} from '../../server/types/patient-profile.types';
import {useUploadPatientProfile} from '../../server/react-query/usePatientProfile';
import {usePullDownRefreshContext} from '../../stores/pullDownRefreshStore';

const schema: yup.Schema<EditAccountReqBodyExt> = yup.object().shape(
  {
    location: yup.string(),
    contactNumber: yup
      .string()
      .trim()
      .matches(/^[\d +-]+$/, {
        message: 'Invalid contact number format',
        excludeEmptyString: true,
      }),
    oldPassword: yup
      .string()
      .trim()
      .when('newPassword', {
        is: (newPassword: string) => newPassword && newPassword.length > 0,
        then: yup
          .string()
          .required('Field is required')
          .min(6, 'Min of 6 characters.'),
      }),
    newPassword: yup
      .string()
      .trim()
      .when('oldPassword', {
        is: (oldPassword: string) => oldPassword && oldPassword.length > 0,
        then: yup
          .string()
          .required('Field is required')
          .min(6, 'Min of 6 characters.'),
      }),
    confirmNewPassword: yup
      .string()
      .trim()
      .when('newPassword', {
        is: (newPassword: string) => newPassword && newPassword.length > 0,
        then: yup
          .string()
          .required('This field is required.')
          .min(6, 'Min of 6 characters.')
          .oneOf([yup.ref('newPassword'), null], 'Passwords do not match.'),
      }),
  },
  [['oldPassword', 'newPassword']],
);

const PatientEditAccountScreen = () => {
  const navigation = useNavigation();
  const {authStore} = useStores();
  const {
    control,
    handleSubmit,
    getValues,
    reset,
    setValue,
    formState: {errors, isValid},
  } = useForm<EditAccountReqBodyExt>({
    mode: 'all',
    defaultValues: {
      location: '',
      contactNumber: '',
    },
    resolver: yupResolver(schema),
  });

  const user = authStore.authData?.user!;

  const toast = useToast();

  const [photo, setPhoto] = useState<UploadPatientProfilePhotoReqBody>({
    image: null,
  });

  const [fetchedPhoto, setFetchedPhoto] = useState('');

  const patientId: PatientProfileReqBody = {
    patientId: user.patientId,
  };

  const mutation = useEditAccount(getValues());
  const uploadMutation = useUploadPatientProfile(photo, patientId.patientId);

  const editPatientProfile = () => {
    handleSubmit(onSubmit)();
  };

  const onSubmit = async () => {
    if (isValid) {
      try {
        await mutation.mutateAsync();

        if (photo.image) {
          await uploadMutation.mutateAsync();
          uploadMutation.reset();
        }

        reset();
        mutation.reset();
        goToViewProfile();
      } catch (error) {
        if (error?.data?.error) {
          toast?.show(error?.data?.error, {type: 'danger'});
        }
      }
    }
  };

  const {setCanRefresh} = usePullDownRefreshContext();

  const goToViewProfile = () => {
    navigation.navigate(NavigationKey.PATIENT_ACCOUNT);
  };

  const getFile = (file: any | null) => {
    setPhoto({
      image: file,
    });
  };

  useEffect(() => {
    setValue('location', user.location);
    setValue('contactNumber', user.contactNumber);
    setFetchedPhoto(user.photo?.url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const [scrollViewHeight, setScrollViewHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useFocusEffect(
    useCallback(() => {
      if (scrollViewHeight > 0 && contentHeight > 0) {
        const isScrollable = contentHeight > scrollViewHeight;

        if (!isScrollable) {
          setCanRefresh(true); // always allow pull-down if not scrollable
        } else {
          setCanRefresh(scrollY <= 0); // only allow when scrolled to top
        }
      }
    }, [scrollViewHeight, contentHeight, scrollY, setCanRefresh]),
  );
  const handleScroll = (e: any) => {
    const y = e.nativeEvent.contentOffset.y;
    setScrollY(y);

    const isScrollable = contentHeight > scrollViewHeight;

    if (!isScrollable) {
      setCanRefresh(true); // still always true if no scroll
    } else {
      setCanRefresh(y <= 0); // only true when back at the very top
    }
  };

  return (
    <ScrollView
      onScroll={handleScroll}
      onLayout={e => {
        setScrollViewHeight(e.nativeEvent.layout.height);
      }}
      onContentSizeChange={(contentWidth, contentHeight) => {
        setContentHeight(contentHeight);
      }}>
      <View style={webStyles.mainContainer}>
        <View style={webStyles.topBackground}>
          <View style={webStyles.titleContainer}>
            <RNEText label="Dashboard > " variant="body" color="white" />
            <RNEText
              label="My Account Details"
              variant="body"
              color="white"
              weight="bold"
            />
          </View>
          <View style={webStyles.headerContainer}>
            <ImageSelector
              onSelectPhoto={getFile}
              providedPhoto={fetchedPhoto}
            />
            <View>
              <RNEText
                label="Edit Account Details"
                variant="header"
                color="white"
                weight="bold"
              />
              <View style={webStyles.dateContainer}>
                <RNEText
                  label={`Date Updated: ${formatDateString(user.modifiedOn)}`}
                  variant="body"
                  color="white"
                />
              </View>
            </View>
          </View>
        </View>

        <View style={webStyles.formContainer}>
          <View style={webStyles.formTitleContainer}>
            <RNEText
              label="Edit Account Details"
              variant="body"
              color="black"
              weight="bold"
            />
          </View>

          <View style={webStyles.labelGroupContainer}>
            <View style={webStyles.rowGroupContainer}>
              <View style={webStyles.infoContainer}>
                <View style={webStyles.inputLabel}>
                  <RNEText
                    label="Email Address"
                    variant="button"
                    color="primary"
                    weight="bold"
                  />
                </View>
                <RNEInput
                  placeholder="Email Address"
                  leftIcon={<SVGIcon name="email" />}
                  value={user.email}
                  variant="shadowed"
                  disabled={true}
                />
              </View>

              <View style={webStyles.infoContainer}>
                <View style={webStyles.inputLabel}>
                  <RNEText
                    label="City Address"
                    variant="button"
                    color="primary"
                    weight="bold"
                  />
                </View>
                <Controller
                  control={control}
                  render={({field: {onChange, onBlur, value}}) => (
                    <RNEInput
                      placeholder="City Address"
                      leftIcon={<SVGIcon name="map" />}
                      value={value || ''}
                      onChangeText={text => {
                        onChange(text);
                      }}
                      errorMessage={errors.location?.message}
                      variant="shadowed"
                    />
                  )}
                  name="location"
                />
              </View>

              <View style={webStyles.infoContainer}>
                <View style={webStyles.inputLabel}>
                  <RNEText
                    label="Phone Number"
                    variant="button"
                    color="primary"
                    weight="bold"
                  />
                </View>
                <Controller
                  control={control}
                  render={({field: {onChange, onBlur, value}}) => (
                    <RNEInput
                      placeholder="Phone Number"
                      leftIcon={<SVGIcon name="call-27" />}
                      value={value || ''}
                      onChangeText={text => {
                        onChange(text);
                      }}
                      errorMessage={errors.contactNumber?.message}
                      variant="shadowed"
                    />
                  )}
                  name="contactNumber"
                />
              </View>
            </View>

            <View style={webStyles.rowGroupContainer}>
              <View style={webStyles.infoContainer}>
                <View style={webStyles.inputLabel}>
                  <RNEText
                    label="Current Password"
                    variant="button"
                    color="primary"
                    weight="bold"
                  />
                </View>
                <Controller
                  control={control}
                  render={({field: {onChange, onBlur, value}}) => (
                    <RNEInput
                      placeholder="* * * * * *"
                      secureTextEntry
                      leftIcon={<SVGIcon name="lock" />}
                      value={value || ''}
                      onChangeText={text => {
                        onChange(text);
                      }}
                      errorMessage={errors.oldPassword?.message}
                      variant="shadowed"
                    />
                  )}
                  name="oldPassword"
                />
              </View>

              <View style={webStyles.infoContainer}>
                <View style={webStyles.inputLabel}>
                  <RNEText
                    label="New Password"
                    variant="button"
                    color="primary"
                    weight="bold"
                  />
                </View>
                <Controller
                  control={control}
                  render={({field: {onChange, onBlur, value}}) => (
                    <RNEInput
                      placeholder="* * * * * *"
                      secureTextEntry
                      leftIcon={<SVGIcon name="lock" />}
                      value={value || ''}
                      onChangeText={text => {
                        onChange(text);
                      }}
                      errorMessage={errors.newPassword?.message}
                      variant="shadowed"
                    />
                  )}
                  name="newPassword"
                />
              </View>

              <View style={webStyles.infoContainer}>
                <View style={webStyles.inputLabel}>
                  <RNEText
                    label="Confirm New Password"
                    variant="button"
                    color="primary"
                    weight="bold"
                  />
                </View>
                <Controller
                  control={control}
                  render={({field: {onChange, onBlur, value}}) => (
                    <RNEInput
                      placeholder="* * * * * *"
                      secureTextEntry
                      leftIcon={<SVGIcon name="lock" />}
                      value={value || ''}
                      onChangeText={text => {
                        onChange(text);
                      }}
                      errorMessage={errors.confirmNewPassword?.message}
                      variant="shadowed"
                    />
                  )}
                  name="confirmNewPassword"
                />
              </View>
            </View>
          </View>

          <View style={webStyles.buttonsContainer}>
            <View style={webStyles.buttonContainer}>
              <RNEButton
                title="Cancel"
                color="secondary"
                style={webStyles.buttonStyle}
                variant="outlined"
                onPress={goToViewProfile}
              />
            </View>
            <RNEButton
              title="Save"
              color="secondary"
              style={webStyles.buttonStyle}
              onPress={editPatientProfile}
              loading={mutation.isLoading || uploadMutation.isLoading}
            />
          </View>
        </View>
      </View>
    </ScrollView>
  );
};

export default PatientEditAccountScreen;

const webStyles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    height: '100%',
    width: '100%',
    backgroundColor: '#F6F7FB',
  },
  topBackground: {
    backgroundColor: colorPalette.primary,
    borderBottomRightRadius: 50,
    height: 420,
  },
  titleContainer: {
    flexDirection: 'row',
    marginHorizontal: 100,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: colorPalette.white,
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 140,
    paddingRight: 80,
    paddingVertical: 60,
  },
  userIcon: {
    backgroundColor: colorPalette.white,
    marginRight: 48,
    borderRadius: 100,
    height: 140,
    width: 140,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cameraIcon: {
    backgroundColor: colorPalette.white,
    borderRadius: 50,
    height: 36,
    width: 36,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    top: 160,
    left: 240,
  },
  dateContainer: {
    paddingTop: 12,
  },
  formContainer: {
    backgroundColor: colorPalette.white,
    borderRadius: 20,
    marginHorizontal: 100,
    top: -100,
    paddingVertical: 40,
    paddingHorizontal: 60,
  },
  formTitleContainer: {
    paddingLeft: 24,
  },
  labelGroupContainer: {
    width: '100%',
    marginVertical: 40,
  },
  rowGroupContainer: {
    flexDirection: 'row',
    marginBottom: 60,
  },
  infoContainer: {
    paddingLeft: 24,
    marginBottom: 4,
    flex: 1,
  },
  inputLabel: {
    paddingLeft: 24,
    marginBottom: 4,
  },
  buttonContainer: {
    marginRight: 20,
  },
  buttonsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  buttonStyle: {
    width: 180,
  },
});
